import { computed, ref, unref, watch, type Ref } from 'vue'
import type { Emoji, EmojiConfig } from '../../../shared/model/emoji-config'
import type { UIConfig } from '../model/ui-config'

interface RenderPlanItemBase {
  key: number | string
}

interface RenderPlanItemHeader extends RenderPlanItemBase {
  type: 'header'
  text: string
}

interface RenderPlanItemEmojis extends RenderPlanItemBase {
  type: 'emoji'
  emojis: Emoji[]
}

type RenderPlanItem = RenderPlanItemHeader | RenderPlanItemEmojis

interface RenderPlan {
  minSuitableScrollTop: number
  maxSuitableScrollTop: number
  verticalOffset: number
  totalHeight: number
  items: RenderPlanItem[]
}

export function useEmojiRenderPlanner(emojiConfig: Ref<EmojiConfig>, uiConfig: Ref<UIConfig>, scrollTop: Ref<number>) {
  const fullRenderPlan = computed(() => {
    const emojiConfigVal = unref(emojiConfig)
    const uiConfigVal = unref(uiConfig)

    let totalHeight = 0

    const renderPlanItems = [] as RenderPlanItem[]

    for (const category of emojiConfigVal) {
      renderPlanItems.push({
        type: 'header',
        text: category.name,
        key: category.id,
      })

      if (totalHeight !== 0) {
        totalHeight += uiConfigVal.gridGap
      }

      totalHeight += uiConfigVal.headerRowSize

      const emojis = category.content.flatMap(subcategory => subcategory.content)
      for (let i = 0; i < emojis.length; i += uiConfigVal.gridColumnCount) {
        renderPlanItems.push({
          type: 'emoji',
          emojis: emojis.slice(i, i + uiConfigVal.gridColumnCount),
          key: emojis[i].id,
        })

        if (totalHeight !== 0) {
          totalHeight += uiConfigVal.gridGap
        }

        totalHeight += uiConfigVal.emojiSize
      }
    }

    return {
      minSuitableScrollTop: 0,
      maxSuitableScrollTop: Infinity,
      items: renderPlanItems,
      totalHeight,
      verticalOffset: 0,
    }
  })

  const cachedReducedRenderPlan = ref<RenderPlan | null>(null)
  watch([fullRenderPlan], () => (cachedReducedRenderPlan.value = null))

  const reducedRenderPlan = computed(() => {
    const uiConfigVal = unref(uiConfig)
    const fullRenderPlanVal = unref(fullRenderPlan)
    const scrollTopVal = unref(scrollTop)

    if (fullRenderPlanVal.items.length < 20) return fullRenderPlanVal

    const cachedResult = unref(cachedReducedRenderPlan)
    if (
      cachedResult &&
      cachedResult.minSuitableScrollTop < scrollTop.value &&
      scrollTop.value < cachedResult.maxSuitableScrollTop
    )
      return cachedResult

    const renderOffset = uiConfigVal.gridRowCount * uiConfigVal.emojiSize
    const minSuitableScrollTop = Math.max(0, scrollTopVal - renderOffset)
    const maxSuitableScrollTop = scrollTopVal + renderOffset

    const renderPlan = {
      minSuitableScrollTop,
      maxSuitableScrollTop,
      totalHeight: fullRenderPlanVal.totalHeight,
      items: [] as RenderPlanItem[],
      verticalOffset: 0,
    }

    const targetRenderedSize = uiConfigVal.gridRowCount * uiConfigVal.emojiSize + renderOffset * 3

    let index = 0

    for (; index < fullRenderPlanVal.items.length; index++) {
      const renderPlanItem = fullRenderPlanVal.items[index]
      let addedSize = 0

      if (renderPlan.verticalOffset > 0) {
        addedSize += uiConfigVal.gridGap
      }

      if (renderPlanItem.type === 'emoji') {
        addedSize += uiConfigVal.emojiSize
      }

      if (renderPlanItem.type === 'header') {
        addedSize += uiConfigVal.headerRowSize
      }

      if (renderPlan.verticalOffset + addedSize <= minSuitableScrollTop) {
        renderPlan.verticalOffset += addedSize
      } else {
        break
      }
    }

    let renderedSize = 0
    for (; index < fullRenderPlanVal.items.length; index++) {
      const renderPlanItem = fullRenderPlanVal.items[index]
      let addedSize = 0

      if (renderedSize > 0) {
        addedSize += uiConfigVal.gridGap
      }

      if (renderPlanItem.type === 'emoji') {
        addedSize += uiConfigVal.emojiSize
      }

      if (renderPlanItem.type === 'header') {
        addedSize += uiConfigVal.headerRowSize
      }

      if (renderedSize + addedSize <= targetRenderedSize) {
        renderedSize += addedSize
        renderPlan.items.push(renderPlanItem)
      } else {
        break
      }
    }

    cachedReducedRenderPlan.value = renderPlan

    return renderPlan
  })

  return reducedRenderPlan
}
